import {
  Hash,
  FolderNotchIcon,
  TrashIcon,
  Buildings,
  Hammer,
} from "@phosphor-icons/react";
import { Switch } from "@heroui/react";

interface TagSidebarProps {
  tags: { name: string; count: number }[];
  selectedTag: string | null;
  onSelectTag: (tag: string | null) => void;
  notesCount: number;

  // Company-wide toggle
  showCompanyWide: boolean;
  onShowCompanyWideChange: (show: boolean) => void;

  // Buildings section
  buildings: { id: string; count: number }[];
  selectedBuilding: string | null;
  onSelectBuilding: (id: string | null) => void;

  // Renovation projects section
  renovationProjects: { id: number; title: string; count: number }[];
  selectedRenovation: number | null;
  onSelectRenovation: (id: number | null) => void;

  // Trash section
  showTrash: boolean;
  onShowTrashChange: (show: boolean) => void;
  trashCount: number;
}

export const TagSidebar: React.FC<TagSidebarProps> = ({
  tags,
  selectedTag,
  onSelectTag,
  notesCount,
  showCompanyWide,
  onShowCompanyWideChange,
  buildings,
  selectedBuilding,
  onSelectBuilding,
  renovationProjects,
  selectedRenovation,
  onSelectRenovation,
  showTrash,
  onShowTrashChange,
  trashCount,
}) => {
  return (
    <div className="bg-neutral-primary/[0.02] flex h-full flex-col">
      {/* Header */}
      <div className="border-neutral-primary/5 border-b px-4 py-3">
        <h2 className="text-neutral-primary/80 text-sm font-semibold tracking-wide uppercase">
          Notities
        </h2>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* All Notes */}
        <button
          onClick={() => {
            onSelectTag(null);
            onShowTrashChange(false);
          }}
          className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${
            !showTrash && selectedTag === null
              ? "bg-moss-primary/10 text-moss-primary font-medium"
              : "text-neutral-primary/70 hover:bg-neutral-primary/5"
          }`}
        >
          <div className="flex items-center gap-2">
            <FolderNotchIcon
              weight={!showTrash && selectedTag === null ? "fill" : "regular"}
              size={16}
            />
            <span>Alle notities</span>
          </div>
          <span className="text-neutral-primary/40 text-xs">{notesCount}</span>
        </button>

        {/* Trash */}
        <button
          onClick={() => {
            onSelectTag(null);
            onShowTrashChange(true);
          }}
          className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${
            showTrash
              ? "bg-moss-primary/10 text-moss-primary font-medium"
              : "text-neutral-primary/70 hover:bg-neutral-primary/5"
          }`}
        >
          <div className="flex items-center gap-2">
            <TrashIcon weight={showTrash ? "fill" : "regular"} size={16} />
            <span>Prullenbak</span>
          </div>
          <span className="text-neutral-primary/40 text-xs">{trashCount}</span>
        </button>

        {/* Company-wide toggle */}
        <div className="border-neutral-primary/5 mt-2 border-t px-4 py-3">
          <Switch
            size="sm"
            isSelected={showCompanyWide}
            onValueChange={onShowCompanyWideChange}
            classNames={{
              base: "inline-flex flex-row-reverse w-full max-w-full justify-between items-center",
              wrapper: "mr-0 ml-2",
              label: "text-sm text-neutral-primary/70",
            }}
          >
            Company wide notities
          </Switch>
        </div>

        {/* Tags Section */}
        {tags.length > 0 && (
          <>
            <div className="mt-2 px-4 py-3">
              <h3 className="text-neutral-primary/50 text-xs font-semibold tracking-wide uppercase">
                Tags
              </h3>
            </div>
            {tags.map((tag) => (
              <button
                key={tag.name}
                onClick={() =>
                  onSelectTag(selectedTag === tag.name ? null : tag.name)
                }
                className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${
                  selectedTag === tag.name
                    ? "bg-moss-primary/10 text-moss-primary font-medium"
                    : "text-neutral-primary/70 hover:bg-neutral-primary/5"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Hash
                    weight={selectedTag === tag.name ? "bold" : "regular"}
                    size={16}
                  />
                  <span className="truncate">{tag.name}</span>
                </div>
                <span className="text-neutral-primary/40 text-xs">
                  {tag.count}
                </span>
              </button>
            ))}
          </>
        )}

        {/* Buildings Section */}
        {buildings.length > 0 && (
          <>
            <div className="mt-2 px-4 py-3">
              <h3 className="text-neutral-primary/50 text-xs font-semibold tracking-wide uppercase">
                Gebouwen
              </h3>
            </div>
            {buildings.map((building) => (
              <button
                key={building.id}
                onClick={() =>
                  onSelectBuilding(
                    selectedBuilding === building.id ? null : building.id,
                  )
                }
                className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${
                  selectedBuilding === building.id
                    ? "bg-moss-primary/10 text-moss-primary font-medium"
                    : "text-neutral-primary/70 hover:bg-neutral-primary/5"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Buildings
                    weight={
                      selectedBuilding === building.id ? "fill" : "regular"
                    }
                    size={16}
                  />
                  <span className="truncate">{building.id}</span>
                </div>
                <span className="text-neutral-primary/40 text-xs">
                  {building.count}
                </span>
              </button>
            ))}
          </>
        )}

        {/* Renovation Projects Section */}
        {renovationProjects.length > 0 && (
          <>
            <div className="mt-2 px-4 py-3">
              <h3 className="text-neutral-primary/50 text-xs font-semibold tracking-wide uppercase">
                Renovatieprojecten
              </h3>
            </div>
            {renovationProjects.map((project) => (
              <button
                key={project.id}
                onClick={() =>
                  onSelectRenovation(
                    selectedRenovation === project.id ? null : project.id,
                  )
                }
                className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${
                  selectedRenovation === project.id
                    ? "bg-moss-primary/10 text-moss-primary font-medium"
                    : "text-neutral-primary/70 hover:bg-neutral-primary/5"
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Hammer
                    weight={
                      selectedRenovation === project.id ? "fill" : "regular"
                    }
                    size={16}
                  />
                  <span className="truncate">RENO-{project.id}</span>
                </div>
                <span className="text-neutral-primary/40 ml-2 text-xs">
                  {project.count}
                </span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
